import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Play,
  Sparkles,
  BarChart2,
  Megaphone,
  X,
  Copy,
  Check,
  Terminal,
  Zap,
  MessageSquare,
  Send,
} from "lucide-react";
import AnimatedBackground from "./AnimatedBackground";

// ===== ANNOUNCEMENTS DATA =====
const announcements = [
  {
    id: 1,
    type: "feature",
    title: "New: Developer Sandbox",
    message:
      "Test REST APIs, SMPP, and webhook DLRs with ₦50,000 auto-replenishing virtual credits at zero cost.",
    icon: Terminal,
    action: "Try Sandbox",
    link: "https://app.smarthivesms.com/settings?tab=apiKeys",
    color: "from-emerald-600 to-teal-600",
    lightColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dotColor: "bg-emerald-500",
  },
  {
    id: 2,
    type: "feature",
    title: "Embeddable Form & Landing Page Builder",
    message:
      "Create embeddable forms and landing pages for leads capture. Try it now!",
    icon: Sparkles,
    action: "Try Now",
    link: "https://app.smarthivesms.com/widgets/landing-page-builder",
    color: "from-violet-600 to-purple-600",
    lightColor: "bg-violet-50 text-violet-700 border-violet-200",
    dotColor: "bg-violet-500",
  },
  {
    id: 3,
    type: "info",
    title: "New Email Analytics",
    message:
      "Track open rates, click-throughs, and engagement metrics in real-time.",
    icon: BarChart2,
    action: "View Reports",
    link: "https://app.smarthivesms.com/email/analytics",
    color: "from-blue-600 to-cyan-600",
    lightColor: "bg-blue-50 text-blue-700 border-blue-200",
    dotColor: "bg-blue-500",
  },
  {
    id: 4,
    type: "promo",
    title: "Bulk SMS Discount",
    message:
      "Get 10% off your next top-up when you send over 100,000 marketing messages this month.",
    icon: Megaphone,
    action: "Top Up Now",
    link: "https://app.smarthivesms.com/transaction/topup",
    color: "from-amber-500 to-orange-500",
    lightColor: "bg-amber-50 text-amber-700 border-amber-200",
    dotColor: "bg-amber-500",
  },
];

// ===== SLIDES DATA =====
const slides = [
  {
    title: "Africa's Leading Omnichannel Messaging Platform",
    description:
      "Reach your customers with 99% deliverability across SMS, WhatsApp, and Email. Start for free—no credit card required.",
    badge: "New: WhatsApp Business API",
  },
  {
    title: "Verify Users Fast with Email & SMS OTPs",
    description:
      "Secure, instant, and cost-effective two-factor authentication to protect your platform and reduce fraud by 85%.",
    badge: "Security First",
  },
  {
    title: "Built for Developers: Sandbox, REST & SMPP APIs",
    description:
      "Test free in our dedicated sandbox with ₦50,000 auto-replenishing credits. Integrate SMS, SMPP on port 2776, and webhook DLRs with zero wallet charges.",
    badge: "Free Developer Sandbox",
  },
];

// ===== API CODE EXAMPLES =====
const apiExamples = [
  {
    label: "Request",
    lang: "json",
    code: `POST "/api/v1/sms/send" HTTP/1.1
Host: "api.smarthivesms.com"
Content-Type: "application/json"
headers: "x-api-key" : "YOUR_API_KEY"

{
  "recipients": "2348012345678",
  "msg": "Hello from SmarthiveSMS! Your order #12345 has been confirmed.",
  "sender": "SMARTHIVE",
  "route": "MKT"
  "type": "1",
}`,
  },
  {
    label: "Response",
    lang: "json",
    code: `{
  "status": "success",
  "msg_id": "msg_8f3a9b2c1d4e5f6a7b8c9d0e1f2a3b4c",
  "recipient": "2348012345678",
  "message": "SMS accepted, charged, and processing",
    "charged": 9.8,
    "newBalance": 6117.700000000161
}`,
  },
];

// ===== ANNOUNCEMENT BAR COMPONENT =====
export const AnnouncementBar = () => {
  const [current, setCurrent] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % announcements.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [dismissed]);

  if (dismissed) return null;

  const announcement = announcements[current];
  const Icon = announcement.icon;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      className="relative z-50 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-white/5 h-8 sm:h-9 overflow-hidden flex items-center"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="flex items-center justify-between gap-2">
          <AnimatePresence mode="wait">
            <motion.a
              key={announcement.id}
              href={announcement.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2 text-xs sm:text-sm group min-w-0 flex-1 truncate"
            >
              <span
                className={`w-2 h-2 rounded-full ${announcement.dotColor} animate-pulse shrink-0`}
              />
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/60 shrink-0" />
              <span className="text-white/90 font-medium truncate shrink-0">
                {announcement.title}
              </span>
              <span className="hidden md:inline text-white/50 shrink-0">—</span>
              <span className="hidden md:inline text-white/60 truncate">
                {announcement.message}
              </span>
              <span className="text-accent font-semibold text-[10px] sm:text-xs uppercase tracking-wider ml-1 group-hover:underline shrink-0 whitespace-nowrap">
                {announcement.action} →
              </span>
            </motion.a>
          </AnimatePresence>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Dots */}
            <div className="hidden sm:flex items-center gap-1.5">
              {announcements.map((_, i) => (
                <button
                  title="announcement"
                  type="button"
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    i === current ? "bg-white w-4" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
            <button
              title="dismiss"
              type="button"
              onClick={() => setDismissed(true)}
              className="p-1 text-white/40 hover:text-white/80 transition-colors"
            >
              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ===== API CODE PREVIEW COMPONENT =====
const APICodePreview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiExamples[activeTab].code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const highlightCode = (code: string, lang: string) => {
    if (lang === "bash") {
      return code
        .replace(
          /(curl|Host|Content-Type|headers|POST|GET|PUT|DELETE|PATCH)/g,
          '<span class="text-purple-400">$1</span>',
        )
        .replace(/(-H|-d|-X)/g, '<span class="text-yellow-400">$1</span>')
        .replace(/(".*?")/g, '<span class="text-green-400">$1</span>')
        .replace(
          /(https?:\/\/[^\s]+)/g,
          '<span class="text-cyan-400">$1</span>',
        );
    }
    // JSON highlighting
    return code
      .replace(/(".*?")/g, '<span class="text-green-400">$1</span>')
      .replace(/((true|false|null))/g, '<span class="text-blue-400">$1</span>')
      .replace(/(\d+\.?\d*)/g, '<span class="text-orange-400">$1</span>')
      .replace(/([{},\[\]:])/g, '<span class="text-gray-500">$1</span>')
      .replace(
        /(POST|Host|Content-Type|headers|GET|HTTP\/1\.1|Host:|Content-Type:|Authorization:)/g,
        '<span class="text-purple-400">$1</span>',
      )
      .replace(/(Bearer)/g, '<span class="text-yellow-400">$1</span>');
  };

  return (
    <div className="relative">
      {/* Glow effect */}
      <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-accent/20 rounded-3xl blur-2xl" />

      <div className="relative bg-[#0D1117] rounded-2xl border border-white/10 shadow-2xl">
        {/* Window header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
            <div className="ml-3 flex items-center gap-2 px-3 py-1 bg-white/5 rounded-lg">
              <Terminal className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-500 font-mono">
                api.smarthivesms.com
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1">
            {apiExamples.map((ex, i) => (
              <button
                key={ex.label}
                onClick={() => setActiveTab(i)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  i === activeTab
                    ? "bg-primary-500/20 text-primary-400"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {ex.label}
              </button>
            ))}
            <button
              onClick={handleCopy}
              className="ml-2 p-2 text-gray-500 hover:text-white transition-colors"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Code content */}
        <div className="p-6 overflow-x-auto">
          <pre className="text-sm font-mono leading-relaxed">
            <code
              dangerouslySetInnerHTML={{
                __html: highlightCode(
                  apiExamples[activeTab].code,
                  apiExamples[activeTab].lang,
                ),
              }}
            />
          </pre>
        </div>

        {/* Status bar */}
        <div className="px-5 py-2.5 bg-white/5 border-t border-white/5 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              API Status: Operational
            </span>
            <span className="text-gray-700">|</span>
            <span>v2.1.0</span>
          </div>
          <span className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-accent" />
            Sub-100ms latency
          </span>
        </div>
      </div>

      {/* Floating stats cards */}
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-16 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Send className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">99%</div>
            <div className="text-xs text-gray-500">Delivery Rate</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute -top-16 -right-4 bg-white rounded-xl shadow-xl p-4 border border-gray-100"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">&lt;100ms</div>
            <div className="text-xs text-gray-500">API Latency</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ===== MAIN HERO SECTION =====
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const { title, description, badge } = slides[currentSlide];

  return (
    <div className="relative">
      {/* Announcement Bar */}
      {/* <AnnouncementBar /> */}

      {/* Main Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-950 via-dark to-primary-900">
        {/* Animated Background */}
        <AnimatedBackground />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/20 via-transparent to-primary-950/40" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-36">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  {/* Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/10 border border-primary-500/20 rounded-full"
                  >
                    <Sparkles className="w-4 h-4 text-primary-400" />
                    <span className="text-sm font-medium text-primary-300">
                      {badge}
                    </span>
                  </motion.div>

                  {/* Title */}
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-poltawski leading-tight">
                    {title.split(" ").map((word, i) => {
                      const isHighlight = [
                        "Omnichannel",
                        "WhatsApp",
                        "Developers:",
                      ].includes(word.replace(/[^a-zA-Z:]/g, ""));
                      return (
                        <span
                          key={i}
                          className={isHighlight ? "text-accent" : ""}
                        >
                          {word}{" "}
                        </span>
                      );
                    })}
                  </h1>

                  {/* Description */}
                  <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-xl">
                    {description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap gap-4 justify-center md:justify-start"
              >
                <a
                  href="https://app.smarthivesms.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="bg-accent hover:bg-accent-600 text-white px-8 py-6 rounded-2xl shadow-xl shadow-accent/25 hover:shadow-accent/40 transition-all hover:-translate-y-1 text-base font-semibold group"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a
                  href="https://calendly.com/smarthivesms/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/20 text-gray-400 hover:text-gray-100 hover:bg-white/30 px-8 py-6 rounded-2xl transition-all hover:-translate-y-1 text-base font-semibold backdrop-blur-sm"
                  >
                    <Play className="mr-2 w-5 h-5" />
                    Watch Demo
                  </Button>
                </a>
              </motion.div>

              {/* Trust badges */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-6 text-sm text-gray-400"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>99.9% Uptime</span>
                </div>
                <div className="w-px h-4 bg-gray-700" />
                <span>5,000+ Businesses</span>
                <div className="w-px h-4 bg-gray-700" />
                <span>10+ Countries</span>
              </motion.div>
            </div>

            {/* Right - API Code Preview */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="hidden lg:block"
            >
              <APICodePreview />
            </motion.div>
          </div>

          {/* Slide indicators */}
          <div className="flex justify-center gap-3 mt-16">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentSlide(index);
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 10000);
                }}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  index === currentSlide
                    ? "bg-accent w-10"
                    : "bg-white/20 w-4 hover:bg-white/40"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </section>
    </div>
  );
};

export default HeroSection;
